// CF-HIGHROLLER-STAKES-V1: the stake chip ladder + its selection invariant.
//
// Pure module (no React / no i18n) so the invariant is unit-testable without the
// root-Jest `--jsx` / `.tsx` import issue that affects component-importing specs.
//
// Canonical ladder: 0.5 / 2 / 8 / 18 / 38 / 50 TON — lucky-number climb for the
// high-roller launch (capture floor 0.5, gentle 2, lucky climb 8/18/38, whale 50).
// The RENDERED chips are `ladderCappedBy(maxBetTon)` where maxBetTon is parsed from
// the backend's authoritative MAX_BET_NANO (the backend is the source of truth — a
// chip the backend would reject never appears). While MAX_BET_NANO is 10 TON only
// 0.5/2/8 render; raising it to 50e9 reveals 18/38/50.
//
// INVARIANT (CF-HIGHROLLER-STAKES-V1 R2, Scorer 1502): the selected stake must
// always be a member of the RENDERED ladder — never a hidden value (e.g. a stale
// default) outside the visible chips. `DEFAULT_BET_TON` is a ladder member by
// construction, and `snapBetIntoLadder` pulls any out-of-ladder selection back to a
// visible+affordable chip (rounding DOWN, never surfacing a fake/unaffordable pick).
export const BET_LADDER_TON = [0.5, 2, 8, 18, 38, 50];

// The default selected stake. MUST be a member of BET_LADDER_TON so a fresh user
// never has a hidden selection outside the rendered chips.
export const DEFAULT_BET_TON = BET_LADDER_TON[0];

// The visible chips: ladder entries the backend cap allows. Infinite cap (demo /
// pre-load, no real money) shows the whole ladder. If the cap filters everything
// out, fall back to the smallest entry so the UI always has one chip (the Flip
// button still gates on the real cap/affordability downstream).
export function ladderCappedBy(max: number): number[] {
    if (!isFinite(max)) return BET_LADDER_TON;
    const filtered = BET_LADDER_TON.filter(v => v <= max);
    return filtered.length > 0 ? filtered : [BET_LADDER_TON[0]];
}

// Pull the current selection back into the visible+affordable ladder whenever it
// falls outside it — the cap/balance shrank below it, OR it was never a ladder
// member. Rounds DOWN to the largest visible+affordable chip <= the current
// selection (never a surprise jump UP), falling back to the smallest affordable
// chip. When NOTHING is affordable, returns the selection unchanged — masking no
// affordable chip behind a fake selection is worse than letting canSubmitBet gate.
export function snapBetIntoLadder(
    current: number,
    visibleChips: number[],
    bal: number,
    maxBetTon: number,
): number {
    const allowed = visibleChips.filter(a => a <= bal && a <= maxBetTon);
    const outside = current > bal || current > maxBetTon || !visibleChips.includes(current);
    if (!outside || allowed.length === 0) return current;
    const roundedDown = allowed.filter(a => a <= current);
    return roundedDown.length > 0 ? roundedDown[roundedDown.length - 1] : allowed[0];
}
